import { getSupabase } from "../../lib/supabase";

export type ActivityStats = {
  reports_submitted: number;
  stations_verified: number;
  issues_reported: number;
  reports_sparkline: number[];
};

export type RecentActivityEvent = {
  id: string;
  type: "report" | "check_in" | "issue";
  station_id: string;
  station_name: string | null;
  created_at: string;
};

const SPARKLINE_DAYS = 14;
const RECENT_LIMIT = 20;

export async function getMyActivityStats(userId: string): Promise<ActivityStats> {
  const sb = getSupabase();
  const since = fourteenDaysAgoIso();

  const [reportsCount, checkInsCount, issuesCount, recentReports] = await Promise.all([
    sb
      .from("reports")
      .select("*", { count: "exact", head: true })
      .eq("user_id", userId),
    sb
      .from("check_ins")
      .select("*", { count: "exact", head: true })
      .eq("user_id", userId),
    sb
      .from("station_issues")
      .select("*", { count: "exact", head: true })
      .eq("reporter_id", userId),
    sb
      .from("reports")
      .select("created_at")
      .eq("user_id", userId)
      .gte("created_at", since),
  ]);

  throwIfError("reports count", reportsCount.error);
  throwIfError("check_ins count", checkInsCount.error);
  throwIfError("station_issues count", issuesCount.error);
  throwIfError("recent reports", recentReports.error);

  return {
    reports_submitted: reportsCount.count ?? 0,
    stations_verified: checkInsCount.count ?? 0,
    issues_reported: issuesCount.count ?? 0,
    reports_sparkline: bucketByDay(
      (recentReports.data ?? []) as { created_at: string }[],
    ),
  };
}

export async function getMyRecentActivity(userId: string): Promise<RecentActivityEvent[]> {
  const sb = getSupabase();
  const select = "id, station_id, created_at, stations(name)";
  const [reports, checkIns, issues] = await Promise.all([
    sb.from("reports").select(select).eq("user_id", userId).order("created_at", { ascending: false }).limit(RECENT_LIMIT),
    sb.from("check_ins").select("id, station_id, checked_in_at, stations(name)").eq("user_id", userId).order("checked_in_at", { ascending: false }).limit(RECENT_LIMIT),
    sb.from("station_issues").select(select).eq("reporter_id", userId).order("created_at", { ascending: false }).limit(RECENT_LIMIT),
  ]);

  throwIfError("recent reports", reports.error);
  throwIfError("recent check_ins", checkIns.error);
  throwIfError("recent station_issues", issues.error);

  const events: RecentActivityEvent[] = [
    ...rowsToEvents(reports.data, "report"),
    ...rowsToEvents(checkIns.data, "check_in", "checked_in_at"),
    ...rowsToEvents(issues.data, "issue"),
  ];

  events.sort((a, b) => b.created_at.localeCompare(a.created_at));
  return events.slice(0, RECENT_LIMIT);
}

function throwIfError(label: string, error: { message: string } | null): void {
  if (error) throw new Error(`activity ${label} failed: ${error.message}`);
}

type RawRow = {
  id?: string;
  station_id?: string;
  created_at?: string;
  checked_in_at?: string;
  stations?: { name?: string | null } | { name?: string | null }[] | null;
};

function rowsToEvents(
  rows: unknown,
  type: RecentActivityEvent["type"],
  timeField: "created_at" | "checked_in_at" = "created_at",
): RecentActivityEvent[] {
  if (!Array.isArray(rows)) return [];
  return rows
    .filter((r): r is RawRow => r != null && typeof r === "object")
    .map((r) => {
      const station = Array.isArray(r.stations) ? r.stations[0] : r.stations;
      const ts = (r[timeField] ?? "") as string;
      return {
        id: r.id ?? "",
        type,
        station_id: r.station_id ?? "",
        station_name: station?.name ?? null,
        created_at: ts,
      };
    });
}

function fourteenDaysAgoIso(): string {
  return new Date(Date.now() - SPARKLINE_DAYS * 86400_000).toISOString();
}

function bucketByDay(rows: { created_at: string }[]): number[] {
  const buckets = new Array(SPARKLINE_DAYS).fill(0);
  const now = Date.now();
  for (const r of rows) {
    const days = Math.floor((now - new Date(r.created_at).getTime()) / 86400_000);
    if (days >= 0 && days < SPARKLINE_DAYS) {
      buckets[SPARKLINE_DAYS - 1 - days]++;
    }
  }
  return buckets;
}
